import React, { useCallback, useState, useRef, useEffect } from 'react';
import { Container, Graphics, Text } from '@pixi/react';
import { 
  FaBed, 
  FaChair, 
  FaCouch, 
  FaTv, 
  FaUtensils, 
  FaDoorOpen, 
  FaToilet, 
  FaBath, 
  FaTree, 
  FaCar 
} from 'react-icons/fa';
import { Gi3dStairs } from 'react-icons/gi';

const Icon = ({ shape, isSelected, onUpdate, onSelect, stageRef, isBlueprintMode = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  
  // Map icon types to React Icons and their display info
  const iconMap = {
    bed: { icon: FaBed, symbol: '🛏️' },
    chair: { icon: FaChair, symbol: '🪑' },
    couch: { icon: FaCouch, symbol: '🛋️' },
    tv: { icon: FaTv, symbol: '📺' },
    table: { icon: FaUtensils, symbol: '🍽️' },
    door: { icon: FaDoorOpen, symbol: '🚪' },
    toilet: { icon: FaToilet, symbol: '🚽' },
    bathtub: { icon: FaBath, symbol: '🛁' },
    stairs: { icon: Gi3dStairs, symbol: '🪜' },
    tree: { icon: FaTree, symbol: '🌳' },
    car: { icon: FaCar, symbol: '🚗' }
  };

  const getCoordinates = useCallback((clientX, clientY) => {
    const stage = stageRef.current;
    if (!stage) return null;

    const canvas = stage._canvas;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const scaleX = stage.app ? stage.app.screen.width / rect.width : 1;
    const scaleY = stage.app ? stage.app.screen.height / rect.height : 1;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  }, [stageRef]);

  const handleMouseDown = useCallback((event) => {
    if (!shape) return;
    
    event.stopPropagation();
    onSelect();

    let globalPoint;
    if (event.data?.global) {
      globalPoint = event.data.global;
    } else if (event.clientX !== undefined) {
      globalPoint = getCoordinates(event.clientX, event.clientY);
      if (!globalPoint) return;
    } else {
      return;
    }

    setIsDragging(true);
    setDragStart({
      x: globalPoint.x - shape.x,
      y: globalPoint.y - shape.y,
    });
  }, [shape, onSelect, getCoordinates]);

  const handleMouseMove = useCallback((event) => {
    if (!isDragging || !dragStart) return;

    let globalPoint;
    if (event.data?.global) {
      globalPoint = event.data.global;
    } else if (event.clientX !== undefined) {
      globalPoint = getCoordinates(event.clientX, event.clientY);
      if (!globalPoint) return;
    } else {
      return;
    }

    const newX = globalPoint.x - dragStart.x;
    const newY = globalPoint.y - dragStart.y;

    onUpdate({ x: newX, y: newY });
  }, [isDragging, dragStart, onUpdate, getCoordinates]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
  }, []);

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (event) => {
        if (!dragStart) return;
        
        const globalPoint = getCoordinates(event.clientX, event.clientY);
        if (!globalPoint) return;

        const newX = globalPoint.x - dragStart.x;
        const newY = globalPoint.y - dragStart.y;

        onUpdate({ x: newX, y: newY });
      };

      const handleGlobalMouseUp = () => {
        setIsDragging(false);
        setDragStart(null);
      };

      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [isDragging, dragStart, onUpdate, getCoordinates]);

  // Draw icon background and border
  const drawIcon = useCallback((g) => {
    if (!shape) return;
    
    g.clear();
    const size = shape.size || 64;
    const color = isBlueprintMode ? 0xffffff : (shape.color || 0x333333);
    
    // Use shape alpha for preview mode
    const fillAlpha = shape.isPreview ? (shape.alpha || 0.5) * 0.3 : 0.3;
    const lineAlpha = shape.isPreview ? (shape.alpha || 0.5) : 0.8;
    
    // Draw background circle/square based on category
    if (shape.category === 'outdoor') {
      // Circular background for outdoor items
      g.beginFill(color, fillAlpha);
      g.lineStyle(2, color, lineAlpha);
      g.drawCircle(size / 2, size / 2, size / 2 - 2);
    } else {
      // Rounded rectangle background for furniture/fixtures
      g.beginFill(color, fillAlpha);
      g.lineStyle(2, color, lineAlpha);
      g.drawRoundedRect(2, 2, size - 4, size - 4, 8);
    }
    g.endFill();
    
    // Draw selection border if selected (but not for preview)
    if (isSelected && !shape.isPreview) {
      g.lineStyle(3, 0xff6b6b, 1);
      if (shape.category === 'outdoor') {
        g.drawCircle(size / 2, size / 2, size / 2);
      } else {
        g.drawRoundedRect(0, 0, size, size, 8);
      }
      
      // Add selection glow effect
      g.lineStyle(6, 0xff6b6b, 0.3);
      if (shape.category === 'outdoor') {
        g.drawCircle(size / 2, size / 2, size / 2 + 2);
      } else {
        g.drawRoundedRect(-2, -2, size + 4, size + 4, 10);
      }
    }
  }, [shape, isSelected]);

  if (!shape) return null;

  const iconInfo = iconMap[shape.iconType];
  const size = shape.size || 64;

  return (
    <Container
      x={shape.x}
      y={shape.y}
      interactive={true}
      pointerdown={handleMouseDown}
      pointermove={isDragging ? handleMouseMove : undefined}
      pointerup={handleMouseUp}
      cursor="pointer"
    >
      {/* Background and border */}
      <Graphics draw={drawIcon} />
      
      {/* Icon symbol using emoji */}
      <Text
        text={iconInfo?.symbol || '📦'}
        x={size / 2}
        y={size / 2}
        anchor={0.5}
        alpha={shape.isPreview ? (shape.alpha || 0.5) : 1}
        style={{
          fontFamily: 'Arial',
          fontSize: size * 0.4,
          align: 'center',
        }}
      />
      
      {/* Label */}
      {shape.label && (
        <Text
          text={shape.label}
          x={size / 2}
          y={size + 16}
          anchor={0.5}
          style={{
            fontFamily: 'Arial',
            fontSize: 12,
            fill: 0x333333,
            align: 'center',
            fontWeight: 'bold',
          }}
        />
      )}
    </Container>
  );
};

export default Icon; 